// public/js/modules/map/sensor-chart.js
// Right-hand sidebar with sensor-history charts for the currently viewed asset.
//
// Opened by clicking any sensor value on the floating info panel. It always
// shows the same multi-chart overview (which value was clicked does not matter)
// and spans up to 50% of the map width. The actual chart rendering is shared
// with the asset detail page (modules/charts/sensor-charts.js).

import { formatAssetLabel } from '../../utils/asset-utils.js';
import { RANGES, fetchHistoryPoints, renderSensorChartGrid, groupIdOf } from '../charts/sensor-charts.js';
import { deviceLabelMap } from '../../features/assets/slot-utils.js';
import { getAccumAllRaw } from './history.js';
import { stampRssiLevels } from '../../utils/rssi-level.js';
import {
  isEventGroupEnabled,
  getEventThresholds,
  setEventThresholds,
  setEventGroupEnabled,
} from './trail-events.js';

let sidebarEl = null;
let charts = [];
let currentAsset = null;
let currentRangeId = '14d';
let allPoints = [];
let loading = false;
let fetchToken = 0;

function ensureSidebar(map) {
  if (sidebarEl) return sidebarEl;
  sidebarEl = document.createElement('div');
  sidebarEl.className = 'map-sensor-sidebar';
  map.getContainer().appendChild(sidebarEl);
  return sidebarEl;
}

function destroyCharts() {
  charts.forEach(c => { try { c.destroy(); } catch (e) {} });
  charts = [];
}

function buildShell() {
  const ranges = RANGES.map(r =>
    `<button type="button" class="btn btn-sm ${r.id === currentRangeId ? 'btn-primary' : 'btn-outline-secondary'} js-ssr-range" data-range="${r.id}">${r.label}</button>`
  ).join('');

  sidebarEl.innerHTML = `
    <div class="map-sensor-head">
      <div class="me-2" style="min-width:0;">
        <div class="fw-bold text-truncate">${formatAssetLabel(currentAsset)}</div>
        <div class="text-muted small">Sensor history</div>
      </div>
      <button type="button" class="map-sensor-close js-ssr-close" aria-label="Close" title="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <div class="map-sensor-ranges btn-group btn-group-sm">${ranges}</div>
    <div class="map-sensor-body js-ssr-body"></div>`;

  sidebarEl.querySelector('.js-ssr-close')?.addEventListener('click', closeSensorSidebar);
  sidebarEl.querySelectorAll('.js-ssr-range').forEach(btn => {
    btn.addEventListener('click', () => {
      currentRangeId = btn.dataset.range;
      renderCharts();
    });
  });
}

// Event pins cover the WHOLE drawn path: the sidebar's own 14-day fetch plus
// any extra history windows accumulated on the map (deduped by row identity).
function eventPointsSupplier() {
  const seen = new Set();
  const merged = [];
  const push = (p) => {
    const k = `${p.timestamp}#${p.deviceId || ''}`;
    if (seen.has(k)) return;
    seen.add(k);
    merged.push(p._t != null ? p : { ...p, _t: new Date(p.timestamp).getTime() });
  };
  (allPoints || []).forEach(push);
  (getAccumAllRaw() || []).forEach(push);
  // The sidebar's points are already stamped, the map's accumulated ones are
  // not. Re-stamp the merged set in time order so every row carries `_rssiAvg`
  // (and with it `_rssiSpread` / `_rssiFloor`)
  // from the same pass - otherwise the signal group's threshold would match the
  // level on some rows and fall through to the raw read on others.
  merged.sort((a, b) => a._t - b._t);
  return stampRssiLevels(merged);
}

const eventControls = {
  isEnabled: (gid) => isEventGroupEnabled(gid),
  getThresholds: (gid) => getEventThresholds(gid),
  onToggle: (group, on) => {
    setEventGroupEnabled(groupIdOf(group), on,
      { title: group.title, unit: group.unit, series: group.series }, eventPointsSupplier);
  },
  onThreshold: (group, { from, to }) => setEventThresholds(groupIdOf(group), { from, to }),
};

function renderCharts() {
  if (!sidebarEl) return;
  buildShell();
  destroyCharts();

  const body = sidebarEl.querySelector('.js-ssr-body');
  if (loading) {
    body.innerHTML = `<div class="text-muted small p-3"><i class="fa-solid fa-spinner fa-spin me-1"></i> Loading history…</div>`;
    return;
  }
  charts = renderSensorChartGrid(body, allPoints, currentRangeId,
    currentAsset ? deviceLabelMap(currentAsset) : null, { eventControls });
}

// Route cleared (asset switch / panel close) → the event switches were reset;
// re-render an open sidebar so its controls reflect that.
window.addEventListener('routeCleared', () => {
  if (sidebarEl && sidebarEl.style.display !== 'none' && !loading && currentAsset) renderCharts();
});

async function loadHistory(asset) {
  loading = true;
  renderCharts();
  const token = ++fetchToken;
  try {
    const pts = await fetchHistoryPoints(asset.assetUUID);
    if (token !== fetchToken) return;
    allPoints = pts;
  } catch (e) {
    console.warn('[sensor-chart] history load failed', e);
    if (token === fetchToken) allPoints = [];
  } finally {
    if (token === fetchToken) { loading = false; renderCharts(); }
  }
}

export function openSensorSidebar(asset, map) {
  if (!asset || !map) return;
  currentAsset = asset;
  ensureSidebar(map);
  sidebarEl.style.display = 'flex';
  allPoints = [];
  loadHistory(asset);
}

export function closeSensorSidebar() {
  destroyCharts();
  if (sidebarEl) sidebarEl.style.display = 'none';
  currentAsset = null;
  allPoints = [];
  fetchToken++;
}
